import { EqualHeightProductList } from "./EqualHeightProductList";
import { itx, noItx } from "../../translationItx/itx";
import { useEffect, useRef, useState } from "react";
import { catalogProductOptions, latestCatalogProductOptions, type CatalogProductOption } from "./catalogProductOptions";
import { isUiModeExpert, uiModeWatcher } from "../../uiMode";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";
import type { CatalogProductSelection } from "./CatalogProductSelection";
import ProductStorageHandler from "../../persistence/ProductStorageHandle";
import ManufacturerLogo from "../ProductList/ManufacturerLogo";
import { ProductCardText } from "./ProductCardText";
import Select from "react-select";
import "./ProductSelection.scss";

export type CatalogProductType = "Button" | "Actor" | "Sensor" | "DecentralActor" | "Other" | "All";
export type CatalogProductSubType =
  | "Simple" // no itx
  | "Touch" // no itx
  | "Panel" // no itx
  | "Switching" // no itx
  | "Dimming" // no itx
  | "Shading" // no itx
  | "Heating" // no itx
  | "BinaryInputs" // no itx
  | "General"; // no itx

type CatalogProductPickerProps = {
  Type: CatalogProductType;
  SubType?: CatalogProductSubType;
  onProductSelected: (product: CatalogProductSelection) => void;
  onSelectionChange: (product: CatalogProductSelection | undefined) => void;
  disabled?: boolean;
  selectedProduct?: CatalogProductOption;
  onProductChange: (product: CatalogProductOption | undefined) => void;
  onToggleTraditional?: () => void;
  onGeneric?: () => void;
};

const MANUFACTURERS: Partial<Record<CatalogProductType, string[]>> = {
  Button: ["MDT technologies", "GVS", "GIRA Giersiepen", "Lingg & Janke"],
  Actor: ["MDT technologies", "ABB", "GIRA Giersiepen", "Albrecht Jung"],
  Sensor: ["MDT technologies", "GIRA Giersiepen", "Busch-Jaeger Elektro", "Merten"],
};

function VisibleProductImage({ product, fallbackArticleNo }: { product: CatalogProductSelection; fallbackArticleNo?: string }) {
  const { orderNumber, catalogId, variantArticleNumber } = product;
  const placeholderRef = useRef<HTMLDivElement>(null);
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    const placeholder = placeholderRef.current;
    if (!placeholder) return;
    let active = true;
    setImageUrl(undefined);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        void ProductStorageHandler.loadFrontImage({
          orderNumber,
          catalogId: catalogId || "",
          articleNo: variantArticleNumber,
        }).then(async (image) => {
          if (!image && active && !variantArticleNumber && fallbackArticleNo) {
            image = await ProductStorageHandler.loadFrontImage({
              orderNumber,
              catalogId: catalogId || "",
              articleNo: fallbackArticleNo,
            });
          }
          if (active) setImageUrl(image);
        }).catch((error) => {
          if (active) setImageUrl("");
          console.error(noItx("Could not load product image:"), error);
        });
      },
      {
        root: placeholder.closest(".catalog_addProductScroll"),
        rootMargin: "0px",
        threshold: 0.01,
      },
    );
    observer.observe(placeholder);
    return () => {
      active = false;
      observer.disconnect();
    };
  }, [orderNumber, catalogId, variantArticleNumber, fallbackArticleNo]);

  return (
    <div className="catalog_productImagePlaceholder" ref={placeholderRef}>
      {imageUrl ? <img src={imageUrl} alt="" decoding="async" onError={() => setImageUrl("")} />
        : imageUrl === "" && <div className="catalog_productImageFallback">{itx("productSelection.noProductImage", "Kein Bild verfügbar")}</div>}
    </div>
  );
}

/** Selects a catalog product first, then its concrete variant. */
export function CatalogProductPicker({ Type, SubType, onProductSelected, onSelectionChange, disabled = false,
  selectedProduct, onProductChange: setSelectedProduct, onToggleTraditional, onGeneric }: CatalogProductPickerProps) {
  useOnChangeWatcher(uiModeWatcher);
  const expert = isUiModeExpert();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CatalogProductOption[]>([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState<string>();
  const [selectedDevice, setSelectedDevice] = useState<CatalogProductSelection>();
  const [productSearchText, setProductSearchText] = useState("");
  const [variantSearchText, setVariantSearchText] = useState("");
  const searchText = selectedProduct ? variantSearchText : productSearchText;
  const setSearchText = selectedProduct ? setVariantSearchText : setProductSearchText;
  const selectDevice = (product: CatalogProductSelection | undefined) => {
    setSelectedDevice(product);
    onSelectionChange(product);
  };
  const requestGeneration = useRef(0);
  const language = state.settings.uiLanguage;

  useEffect(() => {
    setVariantSearchText("");
    selectDevice(undefined);
  }, [selectedProduct]);

  useEffect(() => {
    const generation = ++requestGeneration.current;
    setLoading(true);
    setData([]);
    setProductSearchText("");
    setVariantSearchText("");
    setSelectedManufacturer(undefined);
    setSelectedProduct(undefined);
    selectDevice(undefined);

    const loadProducts = async () => {
      try {
        const metadata = await ProductStorageHandler.fetchAllMetadata("import");
        if (generation !== requestGeneration.current) return;

        const options = catalogProductOptions(metadata, language);
        const products = expert ? options : latestCatalogProductOptions(options);
        setData(products);
        const availableManufacturers = [...new Set(products
          .filter(option => (Type === "All" || option.productType === Type))
          .map(option => option.product.manufacturer).filter(Boolean))]
          .sort((a, b) => a.localeCompare(b, language));
        setSelectedManufacturer((MANUFACTURERS[Type] ?? []).find(name => availableManufacturers.includes(name))
          ?? availableManufacturers[0]);
      } catch (error) {
        console.error(noItx("Could not load product metadata:"), error);
      } finally {
        if (generation === requestGeneration.current) setLoading(false);
      }
    };
    void loadProducts();
    return () => {
      requestGeneration.current++;
    };
  }, [Type, SubType, language, expert]);

  const categoryProducts = data.filter(option => (Type === "All" || option.productType === Type)).map(option => {
    if (option.variants.length !== 1) return option;
    const variant = option.variants[0];
    return { ...option, product: variant.product, description: variant.description || option.description };
  });
  const normalizeSearch = (text: string) => text.toLocaleLowerCase(language).replace(/\s+/g, " ").trim();
  const searchTerms = normalizeSearch(searchText).split(" ").filter(Boolean);
  const matchesSearch = ({ product, description }: { product: CatalogProductSelection; description: string }) =>
    searchTerms.every(term => [description, product.variantArticleNumber || product.orderNumber]
      .some(text => normalizeSearch(text).includes(term)));
  const visibleProducts = categoryProducts.filter(option =>
    (!selectedManufacturer || option.product.manufacturer === selectedManufacturer) && matchesSearch(option));
  const visibleVariants = selectedProduct?.variants.filter(matchesSearch) ?? [];
  const productItems = selectedProduct
    ? visibleVariants.map(({ product, description, variantEan }, index) => (
        <button type="button" className="estadaColumn Item catalog_variantProductCard"
          key={`${product.catalogId}-${product.variantArticleNumber}-${index}`}
          disabled={disabled}
          aria-pressed={selectedDevice === product}
          onClick={() => selectDevice(product)}
          onDoubleClick={() => { if (!disabled) onProductSelected(product); }}>
          <VisibleProductImage product={product} />
          <div className="catalog_variantProductText">
            <ProductCardText description={description} orderNumber={product.variantArticleNumber || product.orderNumber}
              variantEan={variantEan}
              applicationVersion={selectedProduct.productInfo.ApplicationVersion} />
            {!selectedManufacturer && <div>{product.manufacturer}</div>}
          </div>
        </button>
      ))
    : visibleProducts.map((option, index) => {
        const { product, variants, description } = option;
        return (
          <button type="button" className="estadaColumn Item" key={`${product.catalogId}-${index}`}
            disabled={disabled}
            aria-pressed={selectedDevice === product}
            onClick={() => {
              if (variants.length > 1) {
                selectDevice(undefined);
                setSelectedProduct(option);
                setVariantSearchText("");
              } else selectDevice(product);
            }}
            onDoubleClick={() => { if (!disabled && variants.length <= 1) onProductSelected(product); }}>
            <VisibleProductImage product={product} fallbackArticleNo={variants[0]?.product.variantArticleNumber} />
            <ProductCardText description={description} orderNumber={product.variantArticleNumber || product.orderNumber}
              applicationVersion={option.productInfo.ApplicationVersion}
              variantEan={variants.length === 1 ? variants[0].variantEan : undefined} />
            {!selectedManufacturer && <div>{product.manufacturer}</div>}
            {variants.length > 1 && <span className="catalog_productVariantCount"
              aria-label={itx("productSelection.productVariantCount", "Varianten: {{count}}", { "{{count}}": String(variants.length) })}
              data-tooltip-id="tv-tooltip"
              data-tooltip-content={itx("productSelection.productVariantCount", "Varianten: {{count}}", { "{{count}}": String(variants.length) })}
              data-tooltip-place="top" // no itx
              >
              {variants.length}
            </span>}
          </button>
        );
      });

  const manufacturers = [...new Set(categoryProducts.map(option => option.product.manufacturer).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, language));
  const fixedManufacturers = (MANUFACTURERS[Type] ?? []).filter(name => manufacturers.includes(name));
  const otherManufacturers = manufacturers.filter(name => !(MANUFACTURERS[Type] ?? []).includes(name));
  const inlineManufacturers = otherManufacturers.length <= 2
    ? [...fixedManufacturers, ...otherManufacturers] : fixedManufacturers;
  const chooseManufacturer = (manufacturer: string | undefined) => {
    if (disabled) return;
    setSelectedManufacturer(manufacturer);
    setSelectedProduct(undefined);
    selectDevice(undefined);
  };
  const manufacturerItems = inlineManufacturers.map((manufacturer) => (
    <div
      className={selectedManufacturer === manufacturer ? "selected" : undefined}
      title={manufacturer}
      aria-pressed={selectedManufacturer === manufacturer}
      onClick={() => chooseManufacturer(manufacturer)}
      key={manufacturer}
    >
      <ManufacturerLogo name={manufacturer} />
    </div>
  ));
  const allManufacturersOption = { value: "", label: itx("menuBar.all", "Alle") };

  return (
    <div className="catalog_addProductContent">
      <div className="estadaRow catalog_productManufacturers" style={{ gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
        {onGeneric && <button type="button" className="catalog_genericButton" data-testid="catalog-generic-product"
          disabled={disabled} onClick={onGeneric}>{itx("productSelection.generic", "Generic")}</button>}
        {selectedProduct
          ? <span>{itx("productSelection.selectProductVariant", "Variante auswählen")}: {selectedProduct.product.orderNumber}</span>
          : manufacturerItems}
        {!selectedProduct && otherManufacturers.length > 2 && (
          <Select
            className="catalog_otherManufacturers"
            aria-label={itx("productSelection.otherManufacturers", "Weitere Hersteller")}
            placeholder={itx("productSelection.otherManufacturers", "Weitere Hersteller")}
            options={[allManufacturersOption, ...otherManufacturers.map(name => ({ value: name, label: name }))]}
            value={!selectedManufacturer ? allManufacturersOption : otherManufacturers.includes(selectedManufacturer)
              ? { value: selectedManufacturer, label: selectedManufacturer } : null}
            onChange={option => chooseManufacturer(option?.value || undefined)}
            isDisabled={disabled}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              container: base => ({ ...base, width: 220, maxWidth: "100%", padding: 0, display: "block", background: "transparent", flex: "0 1 220px" }),
              menuPortal: base => ({ ...base, zIndex: 100000 }),
            }}
            formatOptionLabel={option => (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {option.value && <ManufacturerLogo name={option.value} />}
                <span>{option.label}</span>
              </div>
            )}
          />
        )}
        <label className="catalog_productSearchControl">
          <i className="las la-filter" aria-hidden="true" />
          <input type="search" className="catalog_productSearch"
          aria-label={itx("productSelection.productSearch", "Beschreibung oder Produktnummer filtern")}
          value={searchText} disabled={disabled}
          onChange={event => {
            setSearchText(event.target.value);
            selectDevice(undefined);
          }} />
        </label>
        {onToggleTraditional && <button type="button" className="catalog_viewToggle"
          data-testid="catalog-traditional-view" disabled={disabled}
          title={itx("productSelection.traditionalList", "Traditionelle Liste")}
          aria-label={itx("productSelection.traditionalList", "Traditionelle Liste")}
          onClick={onToggleTraditional}><i className="las la-list" aria-hidden="true" /></button>}
      </div>

      <div className="catalog_addProductScroll">
        <EqualHeightProductList className="catalog_addProductSelection" key={`${Type}-${SubType}-${selectedManufacturer}-${selectedProduct?.product.catalogId ?? ""}`}>
          {loading && <span>{itx("productSelection.Produkte_werden_geladen", "Produkte werden geladen …")}</span>}
          {!loading && (selectedProduct ? visibleVariants : visibleProducts).length === 0 && <span>{itx("productSelection.Keine_Produkte_gefunden", "Keine Produkte gefunden")}</span>}

          {productItems}
        </EqualHeightProductList>
      </div>
    </div>
  );
}
